/**
 * Locale System — Centralized type definitions, constants, and resolution functions.
 *
 * All locale/language logic flows through this file. No hardcoded locale strings
 * should exist elsewhere in the codebase.
 *
 * Type aliases are `string` — they document intent (source vs target) but allow
 * any locale value. Validation happens at runtime via courseStore-backed functions.
 *
 * See: docs/specs/mobile/locale-system.mobile.md
 */

// ─── Source Locales ──────────────────────────────────────────────
// User's native language variants (BCP 47).

export type SourceLocale = string;

// ─── Target Locales ──────────────────────────────────────────────
// Language being learned (BCP 47).

export type TargetLocale = string;

// ─── Unions ──────────────────────────────────────────────────────

export type AnyLocale = string;

// ─── Preferences ─────────────────────────────────────────────────

/** User picks direction for UI language: "use my language" or "use the language I'm learning" */
export type UILanguagePreference = 'source' | 'target';

/** Which side of bilingual content to display in lessons */
export type LessonLanguage = 'source' | 'target';

// ─── Language Courses ────────────────────────────────────────────

export interface LanguageCourse {
  id?: string;
  sourceBase: string;
  targetBase: string;
  defaultSourceLocale: string;
  defaultTargetLocale: string;
  sourceVariants: string[];
  targetVariants: string[];
  hasLessons?: boolean;
}

// ─── Default Variant per Base Language ────────────────────────────
// Derived from courses: each course declares its default source and
// target locale. Updated at runtime when courseStore loads from cache/API.

/** Build a default-variants map from a courses array. */
export function buildDefaultVariants(courses: LanguageCourse[]): Record<string, string> {
  return Object.fromEntries(
    courses.flatMap((c) => [
      [c.sourceBase, c.defaultSourceLocale],
      [c.targetBase, c.defaultTargetLocale],
    ]),
  );
}

let _defaultVariants: Record<string, string> = {};

/** Get the current default-variants map (updated by courseStore). */
export function getDefaultVariants(): Record<string, string> {
  return _defaultVariants;
}

/** Update default variants from fresh course data. Called by courseStore after load. */
export function setDefaultVariants(courses: LanguageCourse[]): void {
  _defaultVariants = buildDefaultVariants(courses);
}

/** Get the default variant for a base language. */
export function getDefaultVariant(base: string): string {
  return _defaultVariants[base] ?? '';
}

/**
 * Build the fallback chain for a locale.
 * Chain: default variant for same base (if different) → DEFAULT_UI_LANGUAGE (if different).
 * Used by i18n fallbackLng config and any other locale resolution.
 */
export function getFallbackChain(locale: string): string[] {
  const chain: string[] = [];
  const defaultVariant = _defaultVariants[getBaseLanguage(locale)];
  if (defaultVariant && defaultVariant !== locale) chain.push(defaultVariant);
  if (DEFAULT_UI_LANGUAGE !== locale && DEFAULT_UI_LANGUAGE !== defaultVariant) {
    chain.push(DEFAULT_UI_LANGUAGE);
  }
  return chain;
}

/**
 * Build the i18n fallbackLng config from a list of locale codes.
 * Returns a Record keyed by locale with fallback arrays, plus a 'default' key.
 */
export function buildI18nFallbacks(localeCodes: string[]): Record<string, string[]> {
  const fallbacks: Record<string, string[]> = {};
  for (const locale of localeCodes) {
    const chain = getFallbackChain(locale);
    if (chain.length > 0) fallbacks[locale] = chain;
  }
  fallbacks.default = [DEFAULT_UI_LANGUAGE];
  return fallbacks;
}

// ─── Constants ───────────────────────────────────────────────────

export const DEFAULT_UI_LANGUAGE = 'en-US';
export const DEFAULT_LESSON_LANGUAGE: LessonLanguage = 'source';

/**
 * The flagship course (Spanish speakers learning English). Used as the
 * explicit anchor for validation fallbacks and course-id defaults — never
 * rely on course-list ordering for this (the backend orders alphabetically
 * by id, so courses[0] is whatever sorts first, currently a lesson-less
 * course).
 */
export const FLAGSHIP_COURSE_ID = 'es-en';

// ─── Locale Metadata ─────────────────────────────────────────────

export interface NativeLocaleInfo {
  /** Locale's own-language name: "Español", "English" */
  language: string;
  /** Variant label in own language: "Latinoamérica", "United States" */
  variant: string;
  /** Variant hint in own language: "tú/ustedes", "color, organize" */
  variantHint: string;
  /** Pronunciation test phrase in own language */
  testPhrase: string;
}

export interface AzureVoice {
  id: string;
  label: string;
}

/** Per-language pronunciation assessment feature flags from languages.json */
export interface PronunciationFeatures {
  /** Named IPA/SAPI phoneme symbols available (not just scores) */
  phonemeLabels: boolean;
  /** Syllable grouping available (en-US only) */
  syllables: boolean;
  /** NBest phoneme alternatives — "what was actually spoken" (en-US only) */
  nbestPhonemes: boolean;
  /** Prosody scoring + per-word break/intonation feedback (en-US only) */
  prosody: boolean;
}

export interface AzureSpeechConfig {
  /** Azure TTS voice name — present when capabilities.tts is true */
  ttsVoice?: string;
  /** Azure STT locale for speech-to-text — present when capabilities.stt is true */
  sttLocale?: string;
  /** Available Azure Neural TTS voices for this locale */
  availableVoices?: AzureVoice[];
  /** Pronunciation assessment feature flags — present when capabilities.pronunciation is true */
  pronunciationFeatures?: PronunciationFeatures;
}

export interface LanguageCapabilities {
  /** Text-to-Speech available from at least one provider */
  tts: boolean;
  /** Speech-to-Text available from at least one provider */
  stt: boolean;
  /** Pronunciation assessment available from at least one provider */
  pronunciation: boolean;
  /** Speech translation available from at least one provider */
  translation: boolean;
}

export interface SpeechConfig {
  azure?: AzureSpeechConfig;
}

export interface LocaleMetadata {
  /** Speech provider configs (TTS voices, STT locales, pronunciation assessment) */
  speech: SpeechConfig;
}

/** Full locale metadata as returned by GET /api/courses. */
export interface LocaleMetadataFull extends LocaleMetadata {
  /** Locale code (e.g. 'es-419', 'en-US') */
  code: string;
  /** Provider-agnostic capability flags */
  capabilities: LanguageCapabilities;
  native: NativeLocaleInfo;
}

/** Resolved locale context for the active course. Computed once on course change. */
export interface ResolvedCourse {
  course: LanguageCourse;
  sourceLocale: LocaleMetadataFull;
  targetLocale: LocaleMetadataFull;
}

// ─── Resolution Functions ────────────────────────────────────────

/** Extract base language from any locale. 'es-MX' → 'es', 'en-US' → 'en' */
export function getBaseLanguage(locale: string): string {
  return locale.split('-')[0];
}

/** Check if a locale belongs to a base language. Replaces startsWith('es') checks. */
export function isLanguage(locale: string, base: string): boolean {
  return getBaseLanguage(locale) === base;
}

/** Get the active locale for lesson content display. */
export function getActiveLocale(
  lessonLanguage: LessonLanguage,
  sourceLocale: string,
  targetLocale: string,
): string {
  return lessonLanguage === 'target' ? targetLocale : sourceLocale;
}

// ─── Validation Functions ────────────────────────────────────────

/**
 * Coerce a stored/derived string into a valid source locale.
 * Returns the value if it exists in any course's sourceVariants, otherwise
 * the flagship-course fallback. Only for repairing corrupt/invalid values —
 * decision points that can handle "unknown" explicitly should not call this.
 */
export function validateSourceLocale(value: string | null | undefined): string {
  if (!value) return _fallbackSourceLocale();
  const courses = _getCourses();
  const allSource = courses.flatMap((c) => c.sourceVariants);
  return allSource.includes(value) ? value : _fallbackSourceLocale();
}

/**
 * Coerce a stored/derived string into a valid target locale.
 * Returns the value if it exists in any course's targetVariants, otherwise
 * the flagship-course fallback (never a positional pick from the list).
 */
export function validateTargetLocale(value: string | null | undefined): string {
  const courses = _getCourses();
  const allTarget = courses.flatMap((c) => c.targetVariants);
  if (value && allTarget.includes(value)) return value;
  return _fallbackTargetLocale();
}

// ─── Lazy courseStore accessors (avoid circular import) ──────────

function _getCourses(): LanguageCourse[] {
  const { getCourses } = require('../store/courseStore');
  return getCourses();
}

function _fallbackSourceLocale(): string {
  const { fallbackSourceLocale } = require('../store/courseStore');
  return fallbackSourceLocale();
}

function _fallbackTargetLocale(): string {
  const { fallbackTargetLocale } = require('../store/courseStore');
  return fallbackTargetLocale();
}

